/**
 * Technical Specs Hook
 * Manages Technical Spec CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export interface TechnicalSpec {
  id: string;
  projectId: string;
  contractId: string | null;
  title: string;
  version: string;
  description: string | null;
  architecture: Record<string, unknown> | null;
  components: Record<string, unknown> | null;
  dataModels: Record<string, unknown> | null;
  securityReqs: Record<string, unknown> | null;
  performanceReqs: Record<string, unknown> | null;
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  linkedContract?: { id: string; title: string; version: string };
}

// List technical specs
export function useTechnicalSpecs(projectId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['technical-specs', projectId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/technical-specs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch technical specs');
      const data = await response.json();
      return data.data as TechnicalSpec[];
    },
    enabled: !!projectId && !!token,
  });
}

// Get single technical spec
export function useTechnicalSpec(projectId: string, specId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['technical-spec', projectId, specId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/technical-specs/${specId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch technical spec');
      const data = await response.json();
      return data.data as TechnicalSpec;
    },
    enabled: !!projectId && !!specId && !!token,
  });
}

// Create technical spec
export function useCreateTechnicalSpec(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<TechnicalSpec>) => {
      const response = await fetch(`/api/projects/${projectId}/technical-specs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create technical spec');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['technical-specs', projectId] });
    },
  });
}

// Update technical spec
export function useUpdateTechnicalSpec(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ specId, data }: { specId: string; data: Partial<TechnicalSpec> }) => {
      const response = await fetch(`/api/projects/${projectId}/technical-specs/${specId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update technical spec');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['technical-specs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['technical-spec', projectId, variables.specId] });
    },
  });
}

// Update technical spec status
export function useUpdateTechnicalSpecStatus(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ specId, status }: { specId: string; status: TechnicalSpec['status'] }) => {
      const response = await fetch(`/api/projects/${projectId}/technical-specs/${specId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update technical spec status');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['technical-specs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['technical-spec', projectId, variables.specId] });
    },
  });
}

// Delete technical spec
export function useDeleteTechnicalSpec(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (specId: string) => {
      const response = await fetch(`/api/projects/${projectId}/technical-specs/${specId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete technical spec');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['technical-specs', projectId] });
    },
  });
}
